import Elmahio from 'elmah.io.javascript';
import { getConfig } from '../hooks/useConfig';

let logger: Elmahio | null = null;

/**
 * Failures that must always report, even though they also contain one of the transient
 * needles below. SignalR surfaces a total connection failure as "Failed to start the
 * connection: Error: Unable to connect to the server with any of the available
 * transports. ..." (HttpConnection), so the broad 'Failed to start the connection' needle
 * would otherwise suppress the one hub failure that leaves a user unable to use the board.
 * Checked first by isTransientHubDisconnect so it beats the transient list.
 */
const PERSISTENT_HUB_FAILURE = [
  'Unable to connect to the server with any of the available transports',
];

/**
 * Transport-level SignalR drops: laptop sleep, background-tab freeze, Wi-Fi
 * blips, Azure worker recycle. Automatic reconnect handles them; they are not
 * application bugs. Matched against console.error text that @microsoft/signalr
 * and the hub hooks emit — see captureConsoleMinimumLevel below.
 */
const TRANSIENT_HUB_DISCONNECT = [
  'Server timeout elapsed without receiving a message from the server',
  'WebSocket closed with status code: 1006',
  // Cloudflare recycles its WebSocket proxy and closes with 1001 (going away).
  'WebSocket closed with status code: 1001',
  'Failed to complete negotiation with the server',
  'Failed to start the connection',
  'Failed to fetch',
  // Firefox's wording for the same fetch failure Chrome reports as "Failed to fetch".
  'NetworkError when attempting to fetch resource',
  'Server returned an error on close',
  'Connection closed with an error',
  // Every in-flight invoke rejects with this when the connection drops. The drop itself is
  // already logged by the disconnect handlers, so reporting it per pending call adds no signal.
  'Invocation canceled due to the underlying connection being closed',
  // Negotiation succeeded but the handshake was cut short, e.g. the tab navigated away.
  'Server returned handshake error',
  // A single transport attempt failing is expected: SignalR then falls back to the next one.
  // Verified 2026-09-05 that the hub still upgrades to WebSockets (101) through Cloudflare
  // with ARR affinity on, so these are client networks blocking the upgrade, not an outage.
  // Losing every transport is not suppressed — see PERSISTENT_HUB_FAILURE above.
  'Failed to start the transport',
  'WebSocket failed to connect',
  'EventSource failed to connect',
  'net::ERR_',
];

/** True when a client log is a normal hub drop, not an app failure. */
export function isTransientHubDisconnect(text: string | undefined | null): boolean {
  if (!text) {
    return false;
  }
  if (PERSISTENT_HUB_FAILURE.some((needle) => text.includes(needle))) {
    return false;
  }
  return TRANSIENT_HUB_DISCONNECT.some((needle) => text.includes(needle));
}

/** elmah.io filter: return true to skip sending the message. */
export function shouldSkipElmahMessage(msg: { title?: string; detail?: string }): boolean {
  return isTransientHubDisconnect(msg.title) || isTransientHubDisconnect(msg.detail);
}

/**
 * Installs the elmah.io browser logger before React mounts, matching
 * https://docs.elmah.io/logging-to-elmah-io-from-react/
 *
 * `breadcrumbs: true` is the documented opt-in for click/XHR trail.
 * `captureConsoleMinimumLevel: 'error'` forwards `console.error` (how poker
 * and retro report caught SignalR failures) — window.onerror misses those.
 * `application` labels client messages so they can be filtered from server logs.
 * `filter` drops expected hub reconnect noise so a leftover room tab cannot
 * flood the log while a laptop sleeps or a tab is frozen.
 */
export function initializeElmahIo(): Elmahio | null {
  if (logger) {
    return logger;
  }

  const config = getConfig();
  const apiKey = config.ELMAHIO_API_KEY;
  const logId = config.ELMAHIO_LOG_ID;
  if (!apiKey || !logId) {
    return null;
  }

  logger = new Elmahio({
    apiKey,
    logId,
    application: typeof window !== 'undefined' ? window.location.hostname : undefined,
    breadcrumbs: true,
    captureConsoleMinimumLevel: 'error',
    filter: shouldSkipElmahMessage,
  }).on('error', (status, statusText) => {
    // Official sample uses console.log, not console.error — captureConsole
    // would otherwise re-enter elmah.io when the API itself fails.
    console.log('elmah.io.javascript failed to log', status, statusText);
  });

  return logger;
}

/** Shared instance for Error Boundaries and other manual `logger.error` calls. */
export function getElmahIoLogger(): Elmahio | null {
  return logger;
}

/** Clears the singleton so unit tests can re-initialize. */
export function resetElmahIo(): void {
  logger = null;
}
